import React from 'react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuItems,
  PopoverMenuSection,
  PopoverMenuItem,
  PopoverMenuItemButton,
  PopoverMenuHairline,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import HorizontalMenu from 'design-system/src/assets/icons/horizontalMenu.svg?react';
import InfoIcon from 'design-system/src/assets/icons/infoIcon.svg?react';
import { Table, TableHeader as BaseTableHeader, TableCell, Row } from '../Table';
import { type SiteOfCare, BENEFIT_PREFERENCE_TYPE_LABELS } from '../../api';
import { type PracticeBenefitPreference } from './BenefitPreferences';
import { formatAddress } from '../../utils';

const TableHeader = styled(BaseTableHeader)`
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
`;

type BenefitPreferencesTableProps = {
  /** List of practice-level benefit preferences to display */
  preferences: PracticeBenefitPreference[];
  /** List of sites of care for resolving site names */
  sites: SiteOfCare[];
  /** Callback when edit is clicked */
  onEdit: (preference: PracticeBenefitPreference) => void;
  /** Callback when duplicate is clicked */
  onDuplicate: (preference: PracticeBenefitPreference) => void;
  /** Callback when delete is clicked */
  onDelete: (preference: PracticeBenefitPreference) => void;
};

/**
 * Table displaying benefit preferences with columns for Drug, Drug NDC,
 * Site of care, Primary preference, and an actions menu.
 */
const BenefitPreferencesTable: React.FC<BenefitPreferencesTableProps> = ({
  preferences,
  sites,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  if (preferences.length === 0) {
    return (
      <div className="rounded-medium border border-dashed border-grayscale-100 p-4">
        <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
          No preferences have been added
        </Typography>
      </div>
    );
  }

  const getSiteName = (siteId: string | null) => {
    if (!siteId) return '-';
    const site = sites.find((s) => s.id === siteId);
    if (!site) return 'Unknown';
    return formatAddress(
      [site.address_line_1, site.address_line_2].filter(Boolean).join(', '),
      site.city,
      site.state,
      site.zipcode,
    );
  };

  // Sort preferences alphabetically by drug name
  const sortedPreferences = [...preferences].sort((a, b) => a.drug_name.localeCompare(b.drug_name));

  return (
    <div className="w-full">
      <Table>
        <thead>
          <tr>
            <TableHeader>Drug</TableHeader>
            <TableHeader>Drug NDC</TableHeader>
            <TableHeader>Site of care</TableHeader>
            <TableHeader>
              <span className="inline-flex items-center gap-1 leading-none" style={{ font: 'inherit' }}>
                Primary preference
                <Tooltip
                  content="Tandem will always attempt primary preference first. If denied, you will be tasked with the option to initiate the other benefit."
                  childNotInteractive
                  maxWidth="280px"
                >
                  <span className="flex leading-none">
                    <InfoIcon
                      className="size-3.5 text-grayscale-400"
                      aria-label="More information about primary preference"
                    />
                  </span>
                </Tooltip>
              </span>
            </TableHeader>
            <TableHeader $width="48" />
          </tr>
        </thead>
        <tbody>
          {sortedPreferences.map((preference) => (
            <Row key={preference.id}>
              <TableCell>{preference.drug_name}</TableCell>
              <TableCell>{preference.drug_ndc || '-'}</TableCell>
              <TableCell>{getSiteName(preference.site_of_care_id)}</TableCell>
              <TableCell>{BENEFIT_PREFERENCE_TYPE_LABELS[preference.benefits_preference]}</TableCell>
              <TableCell className="w-12 text-right">
                <PopoverMenu>
                  <PopoverMenuButton
                    aria-label={`Actions for ${preference.drug_name} preference`}
                    className="flex size-8 cursor-pointer items-center justify-center rounded-medium border-0 bg-transparent hover:bg-grayscale-50"
                  >
                    <HorizontalMenu aria-hidden />
                  </PopoverMenuButton>
                  <PopoverMenuItems width="200px">
                    <PopoverMenuSection>
                      <PopoverMenuItem>
                        {(itemProps) => (
                          <PopoverMenuItemButton
                            {...itemProps}
                            aria-label={`Edit ${preference.drug_name} preference`}
                            onClick={() => {
                              itemProps.close?.();
                              onEdit(preference);
                            }}
                          >
                            Edit
                          </PopoverMenuItemButton>
                        )}
                      </PopoverMenuItem>
                      <PopoverMenuItem>
                        {(itemProps) => (
                          <PopoverMenuItemButton
                            {...itemProps}
                            aria-label={`Duplicate ${preference.drug_name} preference`}
                            onClick={() => {
                              itemProps.close?.();
                              onDuplicate(preference);
                            }}
                          >
                            Copy to new preference
                          </PopoverMenuItemButton>
                        )}
                      </PopoverMenuItem>
                      <PopoverMenuHairline />
                      <PopoverMenuItem>
                        {(itemProps) => (
                          <PopoverMenuItemButton
                            {...itemProps}
                            aria-label={`Delete ${preference.drug_name} preference`}
                            className="text-red-400"
                            onClick={() => {
                              itemProps.close?.();
                              onDelete(preference);
                            }}
                          >
                            Delete
                          </PopoverMenuItemButton>
                        )}
                      </PopoverMenuItem>
                    </PopoverMenuSection>
                  </PopoverMenuItems>
                </PopoverMenu>
              </TableCell>
            </Row>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default BenefitPreferencesTable;
